/**
 * Schemas for ReploAgent Durable Object WebSocket messages to frontend clients.
 */
import { z } from "zod";

import { agentModelConfigSchema } from "./agentModelConfig";
import { publishPhaseSchema, sessionMetadataSchema } from "./agentSession";
import {
  fileEditedEventSchema,
  messagePartAddedEventSchema,
  messagePartCreatedEventSchema,
  messagePartDeltaEventSchema,
  messagePartUpdatedEventSchema,
  opencodeEventSchema,
} from "./agentTools/opencode";
import { canvasOperationSchema } from "./canvas/operations";
import {
  agentFileNamespaceSchema,
  agentFileVisibilitySchema,
} from "./projectFile";

export const processTypeSchema = z
  .enum([
    "opencode",
    "opencode-sse-producer",
    "opencode-watchdog",
    "bridge",
    "nextjs",
    "provision-sites",
    "process-status-emitter",
    "supervisor",
    "workspace-init",
    "state-sync",
    "daytona",
  ])
  .describe("ProcessType");

export const logLevelSchema = z
  .enum(["debug", "info", "warn", "error", "fatal"])
  .describe("LogLevel");

export const processLogPayloadSchema = z
  .object({
    sandboxId: z.string(),
    processType: processTypeSchema,
    level: logLevelSchema,
    message: z.string(),
    timestamp: z.number(),
  })
  .describe("ProcessLogPayload");

export const processLogMessageSchema = z
  .object({
    type: z.literal("process-log"),
    payload: processLogPayloadSchema,
  })
  .describe("ProcessLogMessage");

export const processStatusSchema = z
  .enum(["stopped", "starting", "ready", "backoff", "restarting", "fatal"])
  .describe("ProcessStatus");

export const sandboxStatusSchema = z
  .enum([
    "buildingSupervisorConfig",
    "requestingSandbox",
    "startingSandbox",
    // (Andrew, 2026-07-02, REPL-28632): resume transitions — restoring restarts the container, rotating re-mints env behind the env guard.
    "restoring",
    "rotating",
    "ready",
    "sendingMessage",
    "streaming",
    "stale",
    "recoveringSupervisor",
    "provisioningError",
  ])
  .describe("SandboxStatus");

export const SANDBOX_LIFECYCLE_STAGES = [
  { key: "buildingSupervisorConfig", label: "Building Config" },
  { key: "requestingSandbox", label: "Requesting Sandbox" },
  { key: "startingSandbox", label: "Creating Sandbox" },
  { key: "ready", label: "Ready" },
] as const;

export const projectSandboxStateSchema = z
  .object({
    sandboxId: z.string().optional(),
    status: sandboxStatusSchema.optional(),
    opencodeWebUiUrl: z
      .string()
      .nullish()
      .describe(
        "Admin-only opencode web UI URL (port 4096) carrying a `daytona-preview` JWT in `?preview_token=…`. Published by the coordinator when opencode reports ready.",
      ),
    opencodeStatus: processStatusSchema.nullish(),
    // Per-site dev-server state keyed by siteId; the coordinator derives preview URLs from the live port + providerSandboxId (never persisted, so they can't drift).
    siteIdToPreviewUrl: z.record(z.string(), z.string()).nullish(),
    siteIdToStatus: z.record(z.string(), processStatusSchema).nullish(),
    // Per-site `next dev` crash detail for sites in `fatal`; seeds the preview error overlay + Fix prompt.
    siteIdToErrorDetail: z.record(z.string(), z.string()).nullish(),
    providerSandboxId: z.string().nullish(),
  })
  .describe("ProjectSandboxState");

export const sessionStatusSchema = z
  .enum(["pending", "busy", "idle", "error", "retry"])
  .describe("SessionStatus");

export const sessionStateSchema = z
  .object({
    status: sessionStatusSchema.nullable(),
    metadata: sessionMetadataSchema,
    pendingQuestionId: z.string().nullable(),
  })
  .describe("SessionState");

export const agentStateSyncPayloadSchema = z
  .object({
    sessions: z.record(z.string(), sessionStateSchema).optional(),
    sandboxState: projectSandboxStateSchema.optional(),
    durableObjectId: z.string().optional(),
  })
  .describe("AgentStateSyncPayload");

export const userStateMessageSchema = z
  .object({
    type: z.literal("user-state"),
    state: agentStateSyncPayloadSchema,
  })
  .describe("UserStateMessage");

// The coordinator forwards sandbox file edits, and message parts that arrive
// before their opencode session is registered, without a coordinator session.
const sessionlessOpencodeEventSchema = z.discriminatedUnion("type", [
  fileEditedEventSchema,
  messagePartUpdatedEventSchema,
  messagePartAddedEventSchema,
  messagePartCreatedEventSchema,
  messagePartDeltaEventSchema,
]);

export const opencodeEventMessageSchema = z
  // Both envelopes share `type`, which a discriminated union option must
  // claim uniquely, so match on `type` first and split by `sessionId` after.
  .looseObject({ type: z.literal("opencode-event") })
  .pipe(
    z.union([
      z.object({
        type: z.literal("opencode-event"),
        payload: opencodeEventSchema,
        sessionId: z.string(),
      }),
      z.object({
        type: z.literal("opencode-event"),
        payload: sessionlessOpencodeEventSchema,
        sessionId: z.never().optional(),
      }),
    ]),
  )
  .describe("OpencodeEventMessage");

export const cacheInvalidationKeySchema = z
  .enum([
    "sandboxFiles",
    "sandboxSkills",
    "homeOnboarding",
    // The three files a hidden session writes for the product to read. They are
    // invisible while they work, so the broadcast is how anyone learns the
    // result exists.
    "brandPreview",
    "detectedIntegrations",
    "generatedSuggestions",
    "proactiveFindings",
    "gitHistory",
    "publishDiff",
    "sites",
    "products",
    // Pushed by bedrock when a project's workflow runs change, so Home refetches instead of polling.
    "workflowRuns",
    // Every part transition and every persona a run writes, so Branding
    // follows a build it did not start instead of polling for one.
    "brandBuild",
  ])
  .describe("CacheInvalidationKey");

export const cacheInvalidationMessageSchema = z
  .object({
    type: z.literal("cache-invalidation"),
    keys: z.array(cacheInvalidationKeySchema),
  })
  .describe("CacheInvalidationMessage");

// Control-plane file-change events emitted by bedrock-api (the single authority
// for the Drive file graph) after a mutation commits, fanned out by the
// per-project ProjectCoordinator DO to every client on the project (the
// collaboration socket), not the per-user compute socket. Metadata only — no
// bytes ride the event; consumers refetch a matched file's content via signed
// URL. Each event carries a namespace-relative `path` so consumer globs and the
// opencode skills-restart check match unchanged.
const fileChangeEventBaseSchema = z.object({
  fileId: z.string(),
  path: z.string(),
  parentId: z.string().nullable(),
  name: z.string(),
  namespace: agentFileNamespaceSchema,
  visibility: agentFileVisibilitySchema,
  contentType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  // The source file's `updatedAt` as epoch-ms. Monotonic per file, so a client
  // drops any event whose version it already holds (duplicate/replay
  // suppression). Optional so a new client tolerates an older server mid
  // rolling deploy: a missing version means "deliver, don't dedup".
  version: z.number().optional(),
});

export const fileChangeEventSchema = z
  .discriminatedUnion("op", [
    fileChangeEventBaseSchema.extend({ op: z.literal("created") }),
    fileChangeEventBaseSchema.extend({
      op: z.literal("updated"),
      // The pre-update location, so listings on BOTH the old and new parent
      // invalidate after a rename/move (and the source side of a graft).
      oldParentId: z.string().nullable(),
      oldPath: z.string(),
      oldName: z.string(),
    }),
    fileChangeEventBaseSchema.extend({ op: z.literal("deleted") }),
  ])
  .describe("FileChangeEvent");

export const filesChangedMessageSchema = z
  .object({
    type: z.literal("files-changed"),
    events: z.array(fileChangeEventSchema),
  })
  .describe("FilesChangedMessage");

// Project-scoped presence, fanned out by the ProjectCoordinator DO (the
// collaboration socket) — "who is in project P right now". Like
// `filesChangedMessageSchema`, this rides the per-project collaboration channel,
// not the per-user compute socket, so it is intentionally NOT part of
// `agentToClientMessageSchema`.
// Which tab a user is viewing, for the global Populous status map.
// Self-reported by the client; cosmetic/telemetry only, never used for authorization.
export const populousActiveTabSchema = z
  .discriminatedUnion("tabType", [
    z.object({ tabType: z.literal("home") }),
    z.object({
      tabType: z.literal("miniapp"),
      miniappId: z.string().min(1).max(64),
      // App-internal path, never a full URL; the cap keeps junk off the wire.
      miniappRoute: z.string().max(256).optional(),
    }),
  ])
  .describe("PopulousActiveTab");

export const projectPresenceUserSchema = z
  .object({
    userId: z.string(),
    displayName: z.string(),
    profileImageUrl: z.string().optional(),
    lastActiveAt: z.number(),
  })
  .describe("ProjectPresenceUser");

// Client → DO: the only client-originated frame the project coordinator
// accepts. With `activeTab` it moves the user's dot on the Populous map;
// without it, it is a heartbeat that only refreshes liveness.
export const populousStatusUpdateMessageSchema = z
  .object({
    type: z.literal("populous-status-update"),
    activeTab: populousActiveTabSchema.optional(),
  })
  .describe("PopulousStatusUpdateMessage");

export const projectPresenceMessageSchema = z
  .object({
    type: z.literal("project-presence"),
    users: z.array(projectPresenceUserSchema),
  })
  .describe("ProjectPresenceMessage");

// Canvas set changes, fanned out by the ProjectCoordinator DO after it applies
// an operation batch. `clientId` is the writer's editor instance, so the writer
// drops its own echo; everyone else applies the operations to the same base.
export const canvasChangedMessageSchema = z
  .object({
    type: z.literal("canvas-changed"),
    setFileId: z.string(),
    revision: z.number().int().nonnegative(),
    clientId: z.string(),
    operations: z.array(canvasOperationSchema),
  })
  .describe("CanvasChangedMessage");

// The coordinator dropped its copy of a set (a write reached the file from
// outside the editor); editors re-open to pick up the file's content.
export const canvasResetMessageSchema = z
  .object({
    type: z.literal("canvas-reset"),
    setFileId: z.string(),
  })
  .describe("CanvasResetMessage");

export const sandboxStateMessageSchema = z
  .object({
    type: z.literal("sandbox-state"),
    state: projectSandboxStateSchema.optional(),
  })
  .describe("SandboxStateMessage");

export const connectedUserSchema = z
  .object({
    userId: z.string(),
    name: z.string(),
    lastActiveAt: z.number().default(0),
  })
  .describe("ConnectedUser");

export const connectedUsersMessageSchema = z
  .object({
    type: z.literal("connected-users"),
    users: z.array(connectedUserSchema),
  })
  .describe("ConnectedUsersMessage");

// Aggregate billing notice broadcast by the coordinator after settling
// per-message credit deductions on `session.idle`. Carries a `kind` so the
// client can render an inline notice (credits exhausted, top-up failed)
// without an extra round-trip; `creditsDeducted` and `newBalanceCredits` let
// the credit-balance widget refresh without an extra RPC.
const billingEventMessageSchema = z
  .object({
    type: z.literal("billing-event"),
    payload: z.object({
      kind: z.enum(["deducted", "credits-exhausted", "topup-failed"]),
      creditsDeducted: z.number(),
      newBalanceCredits: z.number(),
    }),
  })
  .describe("BillingEventMessage");

// NOTE (Max, 2026-04-21): Broadcast by the DO for ALL errors — both RPC
// (synchronous throws from @reploAgentCallable) and background (waitUntil
// failures). Carries the server-side errorId so dev/superuser can copy it
// and look up the error in Sentry. userFacingDetails is only present when
// the error is a UserFacingCloudflareAgentCoordinatorError — regular users
// only see a toast when this field is set.
const agentCoordinatorErrorMessageSchema = z
  .object({
    type: z.literal("agent-coordinator-error"),
    errorId: z.string().optional(),
    errorMessage: z.string(),
    stack: z.string().optional(),
    userFacingDetails: z
      .object({
        title: z.string(),
        description: z.string().optional(),
      })
      .optional(),
  })
  .describe("AgentCoordinatorErrorMessage");

// The display states the agent debugger can force the publish popover into.
// Single source of truth — the coordinator RPC, the debugger tab, and the
// publish store all use the generated `PublishDebugUiState` type so the set
// can't drift.
export const publishDebugUiStateSchema = z
  .enum(["idle", "publishing", "completed", "failed"])
  .describe("PublishDebugUiState");

// Debug-only fault injection. Maps a phase -> the failure kind to synthesize when
// the publish tool reaches it. Only "build" can yield "application" (any non-build
// phase is infra by construction); the tool enforces that regardless of contents.
export const publishFaultKindSchema = z
  .enum(["infra", "application"])
  .describe("PublishFaultKind");

// (Cole, 2026-07-02, REPL-28540): must be partialRecord — zod v4's z.record
// with enum keys requires EVERY key to be present, so a sparse spec like
// {build:"infra"} fails validation and safeParse consumers coerce it to {}.
export const publishFaultSpecSchema = z
  .partialRecord(publishPhaseSchema, publishFaultKindSchema)
  .describe("PublishFaultSpec");

// Debug-only fault injection contract, shared by every layer that can synthesize
// a failure. `params` is deliberately flat scalars: injectors must look up their
// own commands by `kind`, never accept one off the wire.
export const faultSpecSchema = z
  .object({
    id: z.string().min(1),
    layer: z.enum([
      "site-source",
      "provisioning",
      "dev-server",
      "sandbox-host",
      "coordinator",
      "client",
      "editor-shell",
      "curtain",
    ]),
    kind: z.enum(["exec", "sentinel", "coordinator-flag", "client"]),
    label: z.string().min(1),
    params: z.record(
      z.string(),
      z.union([z.string(), z.number(), z.boolean()]),
    ),
    // The first four wire values mirror `previewErrorKindSchema` in bedrock-sdk.
    // This vocabulary must be duplicated because bedrock-sdk depends on schemas.
    expectedScreen: z.enum([
      "serverCrash",
      "originUnreachable",
      "frameTimeout",
      "pageError",
      "repairingCurtain",
      "setupCurtain",
      "fixingCurtain",
      "restartInFlight",
      "none",
    ]),
    variant: z.enum(["real", "spoof"]),
    siteId: z.string().min(1).optional(),
  })
  .describe("FaultSpec");

export const thresholdOverridesSchema = z
  .object({
    frameDeadlineMs: z.number().int().nonnegative().optional(),
    probeSilenceMs: z.number().int().nonnegative().optional(),
    probeTimeoutMs: z.number().int().nonnegative().optional(),
    probeLadderCap: z.number().int().nonnegative().optional(),
    restartWaitMs: z.number().int().nonnegative().optional(),
    startWaitMs: z.number().int().nonnegative().optional(),
  })
  .describe("ThresholdOverrides");

export const getFaultsResultSchema = z
  .object({
    faults: z.array(faultSpecSchema),
    overrides: thresholdOverridesSchema,
  })
  .describe("GetFaultsResult");

// Debug-only: forces a debugger-driven surface into a specific UI state,
// overriding that surface's real signals until reset. A discriminated union on
// `target` so every debug section reuses ONE message type instead of adding its
// own. Deliberately carries no session linkage — it injects display state, not
// real outcomes.
export const debugUiStateSchema = z
  .discriminatedUnion("target", [
    z.object({
      target: z.literal("publish"),
      state: publishDebugUiStateSchema,
      errorMessage: z.string().nullable(),
    }),
    z.object({
      target: z.literal("client-fault"),
      ...getFaultsResultSchema.shape,
    }),
  ])
  .describe("DebugUiState");

export const debugUiStateMessageSchema = z
  .object({
    type: z.literal("debug-ui-state"),
    payload: debugUiStateSchema,
  })
  .describe("DebugUiStateMessage");

// ── Debug RPC surface (REPL-28016 / REPL-28722) ──────────────────────────────
// The coordinator exposes debugging through two gated dispatchers instead of a
// grab-bag of individually-callable `debug*` methods: `debug()` (superuser-only)
// and `testHarness()` (reachable only when REPLO_ENV is ci/test). Each action is
// one variant below; the two unions are the per-endpoint allowlists — the schema
// a caller validates against IS what that surface is allowed to invoke. Bodies
// live in agent-coordinator.ts and delegate to DebugService; this is only the
// wire contract + validation boundary.
const debugActionVariants = {
  execSandbox: z.object({
    action: z.literal("execSandbox"),
    params: z.object({ command: z.string() }),
  }),
  killProcess: z.object({
    action: z.literal("killProcess"),
    params: z.object({ processName: z.string() }),
  }),
  killBridge: z.object({
    action: z.literal("killBridge"),
    params: z.object({}).default({}),
  }),
  terminateSandbox: z.object({
    action: z.literal("terminateSandbox"),
    params: z.object({}).default({}),
  }),
  deleteSandbox: z.object({
    action: z.literal("deleteSandbox"),
    params: z.object({}).default({}),
  }),
  clearSqlite: z.object({
    action: z.literal("clearSqlite"),
    params: z.object({}).default({}),
  }),
  abortDurableObject: z.object({
    action: z.literal("abortDurableObject"),
    params: z.object({}).default({}),
  }),
  getSandboxEnv: z.object({
    action: z.literal("getSandboxEnv"),
    params: z.object({}).default({}),
  }),
  setUiState: z.object({
    action: z.literal("setUiState"),
    params: debugUiStateSchema,
  }),
  setPublishFault: z.object({
    action: z.literal("setPublishFault"),
    params: z.object({ spec: publishFaultSpecSchema }),
  }),
  getPublishFault: z.object({
    action: z.literal("getPublishFault"),
    params: z.object({}).default({}),
  }),
  setFault: z.object({
    action: z.literal("setFault"),
    params: z.object({ spec: faultSpecSchema }),
  }),
  clearFault: z.object({
    action: z.literal("clearFault"),
    params: z.union([
      z.object({ id: z.string().min(1), all: z.never().optional() }),
      z.object({ id: z.never().optional(), all: z.literal(true) }),
    ]),
  }),
  getFaults: z.object({
    action: z.literal("getFaults"),
    params: z.object({}).default({}),
  }),
  startSiteDevServer: z.object({
    action: z.literal("startSiteDevServer"),
    params: z.object({ siteId: z.string().min(1) }),
  }),
  setThresholdOverrides: z.object({
    action: z.literal("setThresholdOverrides"),
    params: z.object({ overrides: thresholdOverridesSchema }),
  }),
  rotateSandboxEnv: z.object({
    action: z.literal("rotateSandboxEnv"),
    params: z.object({}).default({}),
  }),
  wipeAllSitesAndDev: z.object({
    action: z.literal("wipeAllSitesAndDev"),
    params: z.object({ hardDelete: z.boolean().optional() }).default({}),
  }),
  getLastPromptError: z.object({
    action: z.literal("getLastPromptError"),
    params: z.object({}).default({}),
  }),
  getStatus: z.object({
    action: z.literal("getStatus"),
    params: z.object({}).default({}),
  }),
  listSessions: z.object({
    action: z.literal("listSessions"),
    params: z.object({}).default({}),
  }),
  getModelConfig: z.object({
    action: z.literal("getModelConfig"),
    params: z.object({}).default({}),
  }),
  setModelConfig: z.object({
    action: z.literal("setModelConfig"),
    params: z.object({ config: agentModelConfigSchema }),
  }),
  seedIntegrationProject: z.object({
    action: z.literal("seedIntegrationProject"),
    params: z.object({ userId: z.string() }),
  }),
} as const;

// Interactive debugging surface (editor debug panel + agent-cli). Superuser-gated
// server-side. Excludes `seedIntegrationProject` — that only makes sense for the
// test harness.
export const debugActionSchema = z
  .discriminatedUnion("action", [
    debugActionVariants.execSandbox,
    debugActionVariants.killProcess,
    debugActionVariants.killBridge,
    debugActionVariants.terminateSandbox,
    debugActionVariants.deleteSandbox,
    debugActionVariants.clearSqlite,
    debugActionVariants.abortDurableObject,
    debugActionVariants.getSandboxEnv,
    debugActionVariants.setUiState,
    debugActionVariants.setPublishFault,
    debugActionVariants.getPublishFault,
    debugActionVariants.setFault,
    debugActionVariants.clearFault,
    debugActionVariants.getFaults,
    debugActionVariants.startSiteDevServer,
    debugActionVariants.setThresholdOverrides,
    debugActionVariants.rotateSandboxEnv,
    debugActionVariants.wipeAllSitesAndDev,
    debugActionVariants.getLastPromptError,
    debugActionVariants.getStatus,
    debugActionVariants.listSessions,
    debugActionVariants.getModelConfig,
    debugActionVariants.setModelConfig,
  ])
  .describe("DebugAction");

// (Cole, 2026-07-06, REPL-28722): Runtime list of the debug action names, derived
// from debugActionSchema so it can't drift. The editor imports this to route
// dynamic control-table entries through debug() vs a plain callable RPC.
export const DEBUG_ACTION_NAMES = debugActionSchema.options.map(
  (variant) => variant.shape.action.value,
);

// Automated harness surface (integration-check, health-check, vitest integration
// specs). Reachable only when REPLO_ENV is ci/test — the environment is the trust
// boundary, so identity is not required to be a superuser.
export const testHarnessActionSchema = z
  .discriminatedUnion("action", [
    debugActionVariants.getStatus,
    debugActionVariants.listSessions,
    debugActionVariants.getSandboxEnv,
    debugActionVariants.getLastPromptError,
    debugActionVariants.execSandbox,
    debugActionVariants.clearSqlite,
    debugActionVariants.setFault,
    debugActionVariants.clearFault,
    debugActionVariants.getFaults,
    debugActionVariants.startSiteDevServer,
    debugActionVariants.setThresholdOverrides,
    debugActionVariants.deleteSandbox,
    debugActionVariants.seedIntegrationProject,
  ])
  .describe("TestHarnessAction");

// Transport-level RPC response. The bedrock-sdk RealtimeConnection owns request/
// response correlation: a client `callMethod` sends `{ type: "rpc", id, method,
// args }` and the DO replies with this frame, which the SDK matches back to the
// pending promise. Both RPC paths emit it — the agents-framework callable path
// (which adds `done` to mark a streamed result complete) and the custom miniapp
// RPC path (`sendRpcResponse`, no `done`). It is modeled here only so the
// client's message router recognizes a frame the SDK has already consumed,
// instead of warning on an unknown type; consumers never act on it directly.
export const rpcResponseMessageSchema = z
  .object({
    type: z.literal("rpc"),
    id: z.string(),
    success: z.boolean(),
    result: z.unknown().optional(),
    error: z.string().optional(),
    done: z.boolean().optional(),
  })
  .describe("RpcResponseMessage");

// Transport-level keepalive. The client sends `{ type: "ping" }` on a heartbeat
// cadence and the DO's Cloudflare auto-response replies with `{ type: "pong" }`
// (see `setWebSocketAutoResponse` in agent-coordinator.ts). It carries no payload
// and needs no application handling — modeled here so the router recognizes it.
export const pongMessageSchema = z
  .object({
    type: z.literal("pong"),
  })
  .describe("PongMessage");

// Emitted by the coordinator's persist drain once a session's turn is fully durable; clients re-read history on it.
export const sessionHistoryDurableMessageSchema = z
  .object({
    type: z.literal("session-history-durable"),
    sessionId: z.string(),
  })
  .describe("SessionHistoryDurableMessage");

export const agentToClientMessageSchema = z
  .discriminatedUnion("type", [
    opencodeEventMessageSchema,
    userStateMessageSchema,
    processLogMessageSchema,
    cacheInvalidationMessageSchema,
    sandboxStateMessageSchema,
    connectedUsersMessageSchema,
    agentCoordinatorErrorMessageSchema,
    billingEventMessageSchema,
    debugUiStateMessageSchema,
    rpcResponseMessageSchema,
    pongMessageSchema,
    sessionHistoryDurableMessageSchema,
  ])
  .describe("AgentToClientMessage");
